"use client";

/**
 * Transcript side of the working view — the segments view is reused from
 * the pre-pivot UI, rewired to the in-memory session store.
 */
import { useState } from "react";
import { useTranslations } from "next-intl";
import { TranscriptSegmentsView } from "@/components/transcript/transcript-segments-view";
import { ExportMenu } from "@/components/flow/export-menu";
import {
  exportBaseName,
  transcriptToMarkdown,
  transcriptToSrt,
  transcriptToText,
  downloadText,
} from "@/lib/client/exports";
import {
  updateSegments,
  useSessionState,
} from "@/lib/client/session-store";

export function TranscriptPanel() {
  const t = useTranslations("Flow");
  const { segments, audioFile } = useSessionState();
  const [includeTimestamps, setIncludeTimestamps] = useState(true);
  const baseName = exportBaseName(audioFile?.name);
  const wordCount = segments.reduce(
    (count, segment) => count + segment.text.split(/\s+/).filter(Boolean).length,
    0,
  );

  function handleTextChange(segmentId: string, text: string) {
    updateSegments(
      segments.map((segment) =>
        segment.id === segmentId ? { ...segment, text } : segment,
      ),
    );
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px] border bg-card">
      <div className="flex flex-none items-center justify-between gap-2.5 border-b px-4 py-2.5">
        <div className="flex items-baseline gap-2.5">
          <h2 className="text-[13px] font-bold tracking-[0.3px]">
            {t("workspace.transcript")}
          </h2>
          <span className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
            {t("workspace.words", { count: wordCount })}
          </span>
        </div>
        <ExportMenu
          disabled={segments.length === 0}
          toggle={{
            label: t("export.includeTimestamps"),
            checked: includeTimestamps,
            onCheckedChange: setIncludeTimestamps,
          }}
          actions={[
            {
              label: t("export.txt"),
              run: () =>
                downloadText(
                  transcriptToText(segments, { includeTimestamps }),
                  `${baseName}-transcript.txt`,
                ),
            },
            {
              label: t("export.srt"),
              run: () =>
                downloadText(transcriptToSrt(segments), `${baseName}.srt`),
            },
            {
              label: t("export.md"),
              run: () =>
                downloadText(
                  transcriptToMarkdown(segments, { includeTimestamps }),
                  `${baseName}-transcript.md`,
                ),
            },
            {
              label: t("export.copy"),
              run: () =>
                navigator.clipboard.writeText(
                  transcriptToText(segments, { includeTimestamps }),
                ),
              successMessage: t("export.copiedTranscript"),
            },
          ]}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto py-1.5">
        <TranscriptSegmentsView
          segments={segments}
          onSegmentTextChange={handleTextChange}
        />
      </div>
    </section>
  );
}
